import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertTriangle, Clock, Calendar, MapPin } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function SlotPicker({ slots, color, selected, onSelect }) {
  const groups = useMemo(() => {
    const g = {};
    slots.forEach((s) => { (g[s.day_key] ||= { label: s.day_label, slots: [] }).slots.push(s); });
    return g;
  }, [slots]);
  const keys = Object.keys(groups);
  const [day, setDay] = useState(keys[0]);
  const active = groups[day] ? day : keys[0];
  if (!keys.length) return <p className="text-sm text-slate-500 italic" data-testid="public-booking-no-slots">Aucun créneau disponible pour le moment.</p>;
  return (
    <div>
      <div className="flex gap-2 overflow-x-auto pb-2" data-testid="public-booking-days">
        {keys.map((k) => {
          const on = active === k;
          const [dow, ...rest] = groups[k].label.split(' ');
          return (
            <button key={k} onClick={() => { setDay(k); onSelect(null); }} data-testid={`public-booking-day-${k}`}
                    className="flex-shrink-0 px-4 py-3 rounded-lg border text-left min-w-[130px] transition-colors"
                    style={on ? { borderColor: color, background: `${color}12`, color } : {}}>
              <div className="text-xs opacity-70">{dow}</div>
              <div className="text-base font-semibold leading-tight">{rest.join(' ')}</div>
              <div className="text-[11px] opacity-60 mt-0.5">{groups[k].slots.length} créneaux</div>
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mt-4">
        {(groups[active]?.slots || []).map((s) => {
          const on = selected?.start === s.start;
          return (
            <button key={s.start} onClick={() => onSelect(s)} data-testid={`public-booking-slot-${s.label}`}
                    className="py-2 rounded-md border text-sm transition-colors hover:bg-slate-50"
                    style={on ? { background: color, borderColor: color, color: '#fff', fontWeight: 600 } : {}}>
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const Shell = ({ embed, children }) => (
  <div className={`${embed ? '' : 'min-h-screen'} bg-slate-50 flex items-center justify-center p-4`}>{children}</div>
);

export default function PublicBooking() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const embed = sp.get('embed') === '1';
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API_BASE}/public/booking/${slug}`);
      setState({ loading: false, data: r.data, error: null });
      document.title = `${r.data.title} — Reno Resolve`;
    } catch (e) {
      setState({ loading: false, data: null, error: e?.response?.data?.detail || 'Lien indisponible' });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async () => {
    const d = state.data;
    const missing = d.fields.find((f) => f.required && !(form[f.key] || '').trim());
    if (missing) { toast.error(`Champ requis : ${missing.label}`); return; }
    setSending(true);
    try {
      await axios.post(`${API_BASE}/public/booking/${slug}`, { slot_start: slot.start, slot_end: slot.end, data: form });
      setDone(slot);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la réservation');
      if (e?.response?.status === 409) { setSlot(null); load(); }
    } finally { setSending(false); }
  };

  if (state.loading) return <Shell embed={embed}><Loader2 className="w-8 h-8 animate-spin text-[#004AAD]" /></Shell>;
  if (state.error) return (
    <Shell embed={embed}>
      <div className="bg-white p-8 rounded-2xl border border-slate-200 max-w-md text-center" data-testid="public-booking-error">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-slate-900 mb-1">Lien indisponible</h2>
        <p className="text-slate-500 text-sm">{state.error}</p>
      </div>
    </Shell>
  );

  const d = state.data;
  const color = d.color || '#004AAD';
  if (done) {
    const fmt = new Date(done.start).toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
    return (
      <Shell embed={embed}>
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200 max-w-lg text-center" data-testid="public-booking-confirmed">
          <CheckCircle2 className="w-14 h-14 mx-auto mb-3" style={{ color }} />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Rendez-vous confirmé</h2>
          <p className="text-slate-600 text-sm">{d.title}</p>
          <div className="rounded-lg px-4 py-3 my-4 font-bold text-lg capitalize" style={{ background: `${color}12`, color }}>{fmt}</div>
          <p className="text-slate-500 text-sm">Durée : {d.duration_min} minutes{d.location ? ` · ${d.location}` : ''}</p>
          {form.email && <p className="text-xs text-slate-400 mt-6">Un email de confirmation vous a été envoyé.</p>}
        </div>
      </Shell>
    );
  }

  return (
    <div className={`${embed ? '' : 'min-h-screen'} bg-slate-50 pb-10`} data-testid="public-booking-page">
      {!embed && (
        <div className="text-white" style={{ background: color }}>
          <div className="max-w-4xl mx-auto px-6 py-5 flex items-center gap-4">
            <div className="bg-white/95 rounded-xl px-4 py-2.5"><img src="/logo.png" alt="Reno Resolve" className="h-9 w-auto" /></div>
          </div>
        </div>
      )}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="public-booking-title">{d.title}</h1>
          <div className="flex flex-wrap gap-4 text-sm text-slate-500 mt-2">
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{d.duration_min} min</span>
            {d.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{d.location}</span>}
          </div>
          {d.description && <p className="text-sm text-slate-700 mt-3 whitespace-pre-line">{d.description}</p>}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3"><Calendar className="w-5 h-5" style={{ color }} />
            <h2 className="font-bold text-slate-900">Choisissez un créneau</h2></div>
          <SlotPicker slots={d.slots} color={color} selected={slot} onSelect={setSlot} />
        </div>

        {slot && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3" data-testid="public-booking-form">
            <div className="text-sm"><span className="text-slate-500">Créneau : </span>
              <b>{slot.day_label} · {slot.label}</b></div>
            <div className="grid sm:grid-cols-2 gap-3">
              {d.fields.map((f) => (
                <div key={f.key} className={f.key === 'message' || f.key === 'adresse' ? 'sm:col-span-2' : ''}>
                  <label className="text-xs font-medium text-slate-600 block mb-1">{f.label}{f.required ? ' *' : ''}</label>
                  {f.key === 'message' ? (
                    <Textarea rows={3} value={form[f.key] || ''} data-testid={`public-booking-field-${f.key}`}
                              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                  ) : (
                    <Input type={f.key === 'email' ? 'email' : f.key === 'telephone' ? 'tel' : 'text'}
                           value={form[f.key] || ''} data-testid={`public-booking-field-${f.key}`}
                           onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <Button onClick={submit} disabled={sending} style={{ background: color }} className="text-white hover:opacity-90"
                      data-testid="public-booking-submit-btn">
                {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Confirmer le rendez-vous
              </Button>
            </div>
          </div>
        )}
        <p className="text-[11px] text-center text-slate-400">Vos données sont utilisées uniquement pour organiser ce rendez-vous (RGPD · Reno Resolve).</p>
      </div>
    </div>
  );
}
